import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface IslandContainerContextValue {
  container: HTMLElement | null;
  containerRef: React.RefObject<HTMLElement | null>;
}

const IslandContainerContext =
  createContext<IslandContainerContextValue | null>(null);

const EMPTY_CONTAINER_REF: React.RefObject<HTMLElement | null> = {
  current: null,
};

function resolveContainer({
  container,
  containerRef,
  islandName,
}: {
  container?: HTMLElement | null;
  containerRef?: React.RefObject<HTMLElement | null>;
  islandName?: string;
}) {
  if (container) {
    return container;
  }

  if (containerRef?.current) {
    return containerRef.current;
  }

  if (!islandName || typeof document === "undefined") {
    return null;
  }

  return document.querySelector<HTMLElement>(
    `app-island[name="${islandName}"]`,
  );
}

export function IslandContainerProvider({
  children,
  islandName,
  container,
  containerRef,
}: {
  children: React.ReactNode;
  islandName?: string;
  container?: HTMLElement | null;
  containerRef?: React.RefObject<HTMLElement | null>;
}) {
  const internalRef = useRef<HTMLElement | null>(null);
  const [resolvedContainer, setResolvedContainer] =
    useState<HTMLElement | null>(() =>
      resolveContainer({ container, containerRef, islandName }),
    );

  useLayoutEffect(() => {
    const next = resolveContainer({ container, containerRef, islandName });
    setResolvedContainer((current) => (current === next ? current : next));
  }, [container, containerRef, islandName]);

  internalRef.current = resolvedContainer;

  const contextValue = useMemo(
    () => ({
      container: resolvedContainer,
      containerRef: containerRef ?? internalRef,
    }),
    [resolvedContainer, containerRef],
  );

  return (
    <IslandContainerContext.Provider value={contextValue}>
      {children}
    </IslandContainerContext.Provider>
  );
}

export function useIslandContainer() {
  const context = useContext(IslandContainerContext);
  if (!context) {
    return { container: null, containerRef: EMPTY_CONTAINER_REF };
  }
  return context;
}
